import { KeyboardEvent, useMemo } from 'react';
import { AdminSeat, AdminSeatingView, AdminTable } from '../contract';
import { STAGE_GAP, TableLayout, layoutRoom } from '../lib/seat-layout';
import { NameMode, nameExtraOffset, nameGap, nameMode } from '../lib/seat-names';
import { shortName, tableNameLines } from '../lib/seat-text';
import styles from '../styles.module.scss';

/** Màu sơ đồ (SPEC mục 5): trống viền xám nền trắng, có vé xanh HP, tên tay tím nhạt, đang chọn viền vàng. */
export const SEAT_COLOR = {
  emptyFill: '#fff',
  emptyStroke: '#C9CDEB',
  ticket: '#1B2150',
  guest: '#B7A4E8',
  selected: '#F5C451',
  tableFill: '#F4F5FB',
  tableStroke: '#C9CDEB',
  text: '#1B2150',
  muted: '#6B7090',
};

/** Ghế đã có người (vé hoặc tên tay). */
export const isSeatTaken = (s: AdminSeat) => s.ticketId !== null || !!s.guestName;

/** Tên người ngồi ghế: tên trên vé → @username → "Vé #id"; tên tay; ghế trống → null. */
export function seatPersonName(seat: AdminSeat): string | null {
  const t = seat.ticket;
  if (t) return t.displayName || (t.username ? `@${t.username}` : `Vé #${t.id}`);
  if (seat.ticketId !== null) return `Vé #${seat.ticketId}`;
  return seat.guestName || null;
}

/** "Bàn Team HT" — tên đã bắt đầu bằng "Bàn" (vd. "Bàn 3" của Thêm nhanh) thì không thêm lần nữa. */
const tableSpoken = (name: string) => (/^bàn(\s|$)/i.test(name) ? name : `Bàn ${name}`);

/** Cỡ số ghế theo bán kính ghế của bàn. */
const seatFont = (r: number) => (r >= 11 ? 11 : r >= 9 ? 9 : 8);
const NAME_FONT = 9;
const LINE_H = 15;
const SUB_H = 14;

type NamePos = { x: number; y: number; anchor: 'start' | 'middle' | 'end' };

/** Chỉ số 0-based của ghế trong hàng (bàn dài) hoặc trong vòng (bàn tròn) — để so le tên. */
function rowIndex(t: TableLayout, number: number): number {
  if (t.shape === 'ROUND') return number - 1;
  const top = Math.ceil(t.seats.length / 2);
  return number <= top ? number - 1 : number - top - 1;
}

/**
 * Chỗ in tên người, hướng ra ngoài bàn. Bàn dài: trên hàng trên / dưới hàng dưới. Bàn tròn:
 * ghế phía trên / dưới → tên theo bán kính ra ngoài; ghế hai bên → tên ngay trên (nửa trên) hoặc
 * ngay dưới (nửa dưới) ghế, lệch ra ngoài 8 — khe giữa hai bàn kề nhau chỉ ~56 nên tên không được
 * chìa ngang sang bàn bên; ghế nằm ngang tâm thì đành chìa ngang (neo mép trong).
 * Bàn đông (so le): `extra` đẩy tên ghế lẻ ra xa thêm một dòng; bàn tròn khi đó in theo bán kính.
 */
function namePos(
  t: TableLayout,
  s: { x: number; y: number },
  mode: NameMode,
  extra: number
): NamePos {
  const off = t.seatR + 8 + extra;
  if (t.shape === 'LONG') {
    return { x: s.x, y: s.y < t.cy ? s.y - off : s.y + off, anchor: 'middle' };
  }
  const dx = s.x - t.cx;
  const dy = s.y - t.cy;
  const ring = Math.hypot(dx, dy) || 1;
  const cos = dx / ring;
  const sin = dy / ring;
  if (mode === 'stagger') {
    // Tên căn giữa, rộng ~44: ghế hai bên phải đẩy xa hơn (theo |cos|) để chữ không đè lên ghế.
    const r = ring + t.seatR + 4 + Math.abs(cos) * 22 + Math.abs(sin) * 6 + extra;
    return { x: t.cx + cos * r, y: t.cy + sin * r, anchor: 'middle' };
  }
  if (Math.abs(sin) < 0.25) {
    return { x: s.x + Math.sign(cos) * (t.seatR + 3), y: s.y, anchor: cos > 0 ? 'start' : 'end' };
  }
  if (Math.abs(cos) > 0.6) {
    return { x: s.x + Math.sign(cos) * 8, y: s.y + Math.sign(sin) * off, anchor: 'middle' };
  }
  const r = ring + t.seatR + 12;
  return { x: t.cx + cos * r, y: t.cy + sin * r, anchor: 'middle' };
}

interface Props {
  view: AdminSeatingView;
  columns: 2 | 3 | 4;
  selectedSeatId: number | null;
  moveMode: boolean;
  /** Ghế trống nhấp nháy viền (sau khi bấm một vé trong danh sách "chưa có ghế"). */
  flashEmpty?: boolean;
  onSeatClick: (seat: AdminSeat, table: AdminTable, anchor: DOMRect) => void;
}

/** Sơ đồ phòng tiệc vẽ bằng SVG từ layoutRoom (SPEC mục 5, 7). Mỗi ghế là một nút bấm được bằng bàn phím. */
const SeatMap = ({ view, columns, selectedSeatId, moveMode, flashEmpty, onSeatClick }: Props) => {
  const layout = useMemo(() => layoutRoom(view.tables, { columns }), [view.tables, columns]);
  const tableById = useMemo(() => new Map(view.tables.map((t) => [t.id, t])), [view.tables]);
  const { stage } = layout;

  return (
    <svg
      viewBox={`0 0 ${layout.width} ${layout.height}`}
      width="100%"
      preserveAspectRatio="xMidYMin meet"
      role="group"
      aria-label="Sơ đồ chỗ ngồi"
      className={styles.seatSvg}
    >
      <rect
        x={stage.x}
        y={stage.y}
        width={stage.w}
        height={stage.h}
        rx={12}
        fill={SEAT_COLOR.ticket}
      />
      <text
        x={stage.x + stage.w / 2}
        y={stage.y + stage.h / 2}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={15}
        fontWeight={700}
        letterSpacing={6}
        fill="#fff"
      >
        SÂN KHẤU
      </text>

      {layout.tables.length === 0 && (
        <text
          x={stage.x + stage.w / 2}
          y={stage.y + stage.h + STAGE_GAP / 2 + 20}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={14}
          fill={SEAT_COLOR.muted}
        >
          Chưa có bàn — bấm Thêm bàn hoặc Thêm nhanh
        </text>
      )}

      {layout.tables.map((tl) => {
        const table = tableById.get(tl.id);
        if (!table) return null;
        const seatByNumber = new Map(table.seats.map((s) => [s.number, s]));
        const taken = table.seats.filter(isSeatTaken).length;
        const lines = tableNameLines(table.name, tl.shape === 'ROUND' ? 12 : 20);
        const blockH = lines.length * LINE_H + SUB_H;
        const firstY = tl.cy - blockH / 2 + LINE_H / 2;
        // Bàn quá đông thì không in tên (bảng bên phải liệt kê thay), vẫn còn <title>/aria-label.
        const mode = nameMode(nameGap(tl));
        const showNames = mode !== 'hidden';
        // Vòng ghế bàn tròn khép kín (ghế cuối kề ghế 1); hàng bàn dài thì không.
        const ringCount = tl.shape === 'ROUND' ? tl.seats.length : undefined;
        const font = seatFont(tl.seatR);

        return (
          <g key={tl.id}>
            {tl.shape === 'ROUND' ? (
              <circle
                cx={tl.cx}
                cy={tl.cy}
                r={tl.w / 2}
                fill={SEAT_COLOR.tableFill}
                stroke={SEAT_COLOR.tableStroke}
              />
            ) : (
              <rect
                x={tl.cx - tl.w / 2}
                y={tl.cy - tl.h / 2}
                width={tl.w}
                height={tl.h}
                rx={10}
                fill={SEAT_COLOR.tableFill}
                stroke={SEAT_COLOR.tableStroke}
              />
            )}
            <title>{table.name}</title>
            {lines.map((line, i) => (
              <text
                key={i}
                x={tl.cx}
                y={firstY + i * LINE_H}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={13}
                fontWeight={700}
                fill={SEAT_COLOR.text}
              >
                {line}
              </text>
            ))}
            {/* Nhãn phụ trong lòng bàn: dưới bàn (labelY) đã có tên người ghế dưới cùng. */}
            <text
              x={tl.cx}
              y={tl.cy + blockH / 2 - SUB_H / 2 + 1}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={11}
              fill={SEAT_COLOR.muted}
            >
              {`${taken}/${table.capacity}`}
            </text>

            {tl.seats.map((pos) => {
              const seat = seatByNumber.get(pos.number);
              if (!seat) return null;
              const person = seatPersonName(seat);
              const empty = person === null;
              const selected = seat.id === selectedSeatId;
              const fill = empty
                ? SEAT_COLOR.emptyFill
                : seat.ticketId !== null
                ? SEAT_COLOR.ticket
                : SEAT_COLOR.guest;
              const stroke = selected
                ? SEAT_COLOR.selected
                : empty
                ? moveMode
                  ? SEAT_COLOR.selected
                  : SEAT_COLOR.emptyStroke
                : fill;
              const fire = (el: SVGGElement) =>
                onSeatClick(seat, table, el.getBoundingClientRect());
              const onKey = (e: KeyboardEvent<SVGGElement>) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  fire(e.currentTarget);
                }
              };
              const np = namePos(
                tl,
                pos,
                mode,
                nameExtraOffset(mode, rowIndex(tl, pos.number), ringCount)
              );
              const cls = [
                styles.seatG,
                empty && flashEmpty ? styles.seatFlash : '',
                moveMode && !empty && !selected ? styles.seatBlocked : '',
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <g
                  key={seat.id}
                  role="button"
                  tabIndex={0}
                  data-seat-id={seat.id}
                  aria-label={`${tableSpoken(table.name)} ghế ${seat.number}: ${person ?? 'trống'}`}
                  className={cls}
                  onClick={(e) => fire(e.currentTarget)}
                  onKeyDown={onKey}
                >
                  <title>
                    {person ? `${person} — ghế ${seat.number}` : `Ghế ${seat.number} trống`}
                  </title>
                  <circle
                    className={styles.seatDot}
                    cx={pos.x}
                    cy={pos.y}
                    r={tl.seatR}
                    fill={fill}
                    stroke={stroke}
                    strokeWidth={selected ? 3 : empty && moveMode ? 2 : 1.5}
                    strokeDasharray={empty && moveMode && !selected ? '4 3' : undefined}
                  />
                  <text
                    x={pos.x}
                    y={pos.y}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={font}
                    fontWeight={700}
                    fill={
                      empty ? SEAT_COLOR.muted : seat.ticketId !== null ? '#fff' : SEAT_COLOR.text
                    }
                    pointerEvents="none"
                  >
                    {seat.number}
                  </text>
                  {person && showNames && (
                    <text
                      className={styles.seatName}
                      x={np.x}
                      y={np.y}
                      textAnchor={np.anchor}
                      dominantBaseline="central"
                      fontSize={NAME_FONT}
                      fill={SEAT_COLOR.text}
                    >
                      {shortName(person)}
                    </text>
                  )}
                </g>
              );
            })}
          </g>
        );
      })}
    </svg>
  );
};

export default SeatMap;
